import React, { useState } from 'react';
import { Container, Typography, Box, Paper, Link, Divider, Button, Alert, Stack, Tooltip, IconButton } from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { useQuery, gql } from '@apollo/client';
import logo from '../images/logo.png';
import { auth } from '../config/firebase';
import { useAuth } from '../contexts/AuthContext';
import { useDocumentTitle } from '../hooks/useDocumentTitle';

let buildVersion: {
  commitSha: string;
  commitShaFull: string;
  branch: string;
  version: string;
  buildTimestamp: string;
} | null = null;
try {
  // eslint-disable-next-line @typescript-eslint/no-require-imports
  buildVersion = require('../generated/buildVersion').buildVersion;
} catch {
  // Not yet generated (e.g. during local dev without a build)
}

// Simple query to test auth
const TEST_QUERY = gql`
  query TestAuth {
    myPlans {
      id
      goal
    }
  }
`;

function DebugPage() {
  useDocumentTitle('Debug');

  const { user } = useAuth();
  const [testResult, setTestResult] = useState<string | null>(null);
  const { refetch } = useQuery(TEST_QUERY, { skip: true });

  // Test: Force sign out without redirect (simulates expired session)
  const handleForceSignOut = async () => {
    try {
      await auth.signOut();
      setTestResult('✅ Signed out. Now click "Test API Call" to trigger UNAUTHENTICATED error.');
    } catch (error: any) {
      setTestResult(`❌ Error: ${error.message}`);
    }
  };

  // Test: Make an API call (will fail if not authenticated)
  const handleTestApiCall = async () => {
    setTestResult('Making API call...');
    try {
      const result = await refetch();
      if (result.data) {
        setTestResult(`✅ API call succeeded. Found ${result.data.myPlans?.length || 0} plans.`);
      } else if (result.errors) {
        setTestResult(`⚠️ API returned errors: ${result.errors.map(e => e.message).join(', ')}`);
      }
    } catch (error: any) {
      setTestResult(`❌ API call failed: ${error.message}`);
    }
  };

  // Test: Invalidate token by clearing it from memory (advanced)
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const handleInvalidateToken = async () => {
    try {
      // This forces the token to be considered stale
      if (auth.currentUser) {
        // Get token then immediately invalidate by signing out and back conceptually
        setTestResult('Token will be invalid on next request. Try "Test API Call".');
      } else {
        setTestResult('No user logged in.');
      }
    } catch (error: any) {
      setTestResult(`❌ Error: ${error.message}`);
    }
  };
  // Check if running on localhost (development)
  const isDevelopment = window.location.hostname === 'localhost' ||
                        window.location.hostname === '127.0.0.1' ||
                        window.location.hostname === '0.0.0.0';

  const graphqlUrl = isDevelopment
    ? `http://${window.location.hostname}:5010/api/graphql`
    : 'https://us-central1-planapus-d83dd.cloudfunctions.net/api/graphql';

  const helloWorldUrl = isDevelopment
    ? `http://${window.location.hostname}:5001/planapus-d83dd/us-central1/helloWorld`
    : 'https://us-central1-planapus-d83dd.cloudfunctions.net/helloWorld';

  const queueDashboardUrl = isDevelopment
    ? `http://${window.location.hostname}:5010/api/admin/queues`
    : 'https://us-central1-planapus-d83dd.cloudfunctions.net/api/admin/queues';

  return (
    <Container maxWidth="md">
      <Box sx={{ my: 4 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 2, mb: 3 }}>
          <img
            src={logo}
            alt="Planapus Logo"
            style={{ height: '60px', width: 'auto' }}
          />
          <Typography variant="h2" component="h1">
            Debug Info
          </Typography>
        </Box>

        {/* Version Info Section */}
        <Paper elevation={3} sx={{ p: 3, mt: 3 }}>
          <Typography variant="h5" gutterBottom>
            Version Info
          </Typography>
          {buildVersion ? (
            <Box sx={{ display: 'grid', gridTemplateColumns: 'auto 1fr', gap: 1, alignItems: 'center' }}>
              <Typography variant="body2" color="text.secondary"><strong>Version</strong></Typography>
              <Typography variant="body2" fontFamily="monospace">{buildVersion.version}</Typography>

              <Typography variant="body2" color="text.secondary"><strong>Commit</strong></Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Typography variant="body2" fontFamily="monospace">{buildVersion.commitSha}</Typography>
                <Tooltip title="Copy full SHA">
                  <IconButton
                    size="small"
                    onClick={() => navigator.clipboard.writeText(buildVersion!.commitShaFull)}
                  >
                    <ContentCopyIcon sx={{ fontSize: 14 }} />
                  </IconButton>
                </Tooltip>
                <Typography variant="caption" color="text.secondary" fontFamily="monospace">
                  ({buildVersion.commitShaFull})
                </Typography>
              </Box>

              <Typography variant="body2" color="text.secondary"><strong>Branch</strong></Typography>
              <Typography variant="body2" fontFamily="monospace">{buildVersion.branch}</Typography>

              <Typography variant="body2" color="text.secondary"><strong>Built</strong></Typography>
              <Typography variant="body2" fontFamily="monospace">
                {new Date(buildVersion.buildTimestamp).toLocaleString()}
              </Typography>
            </Box>
          ) : (
            <Typography variant="body2" color="text.secondary">
              Version info not available. Run a build to generate it.
            </Typography>
          )}
        </Paper>

        {/* Auth Testing Section */}
        <Paper elevation={3} sx={{ p: 3, mt: 3 }}>
          <Typography variant="h5" gutterBottom>
            Auth Testing
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Test authentication error handling and token refresh flow.
          </Typography>

          <Box sx={{ mb: 2, p: 2, bgcolor: 'grey.100', borderRadius: 1 }}>
            {user ? (
              <Box sx={{ display: 'grid', gridTemplateColumns: 'auto 1fr', gap: 0.5, alignItems: 'center' }}>
                <Typography variant="body2" color="text.secondary"><strong>UID</strong></Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <Typography variant="body2" fontFamily="monospace">{user.uid}</Typography>
                  <Tooltip title="Copy UID">
                    <IconButton size="small" onClick={() => navigator.clipboard.writeText(user.uid)}>
                      <ContentCopyIcon sx={{ fontSize: 14 }} />
                    </IconButton>
                  </Tooltip>
                </Box>

                {user.email && (
                  <>
                    <Typography variant="body2" color="text.secondary"><strong>Email</strong></Typography>
                    <Typography variant="body2">{user.email}{user.emailVerified ? ' (verified)' : ' (unverified)'}</Typography>
                  </>
                )}

                {user.phoneNumber && (
                  <>
                    <Typography variant="body2" color="text.secondary"><strong>Phone</strong></Typography>
                    <Typography variant="body2">{user.phoneNumber}</Typography>
                  </>
                )}

                {user.displayName && (
                  <>
                    <Typography variant="body2" color="text.secondary"><strong>Display Name</strong></Typography>
                    <Typography variant="body2">{user.displayName}</Typography>
                  </>
                )}

                <Typography variant="body2" color="text.secondary"><strong>Provider</strong></Typography>
                <Typography variant="body2">{user.providerData.map(p => p.providerId).join(', ') || 'unknown'}</Typography>

                <Typography variant="body2" color="text.secondary"><strong>Created</strong></Typography>
                <Typography variant="body2">{user.metadata.creationTime ? new Date(user.metadata.creationTime).toLocaleString() : '—'}</Typography>

                <Typography variant="body2" color="text.secondary"><strong>Last Sign-In</strong></Typography>
                <Typography variant="body2">{user.metadata.lastSignInTime ? new Date(user.metadata.lastSignInTime).toLocaleString() : '—'}</Typography>
              </Box>
            ) : (
              <Typography variant="body2">Not logged in</Typography>
            )}
          </Box>

          <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
            <Button
              variant="outlined"
              color="warning"
              onClick={handleForceSignOut}
              disabled={!user}
            >
              1. Force Sign Out
            </Button>
            <Button
              variant="outlined"
              onClick={handleTestApiCall}
            >
              2. Test API Call
            </Button>
          </Stack>

          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
            Steps to test: 1) Click "Force Sign Out" 2) Click "Test API Call" → Should redirect to /auth
          </Typography>

          {testResult && (
            <Alert severity={testResult.startsWith('✅') ? 'success' : testResult.startsWith('❌') ? 'error' : 'info'} sx={{ mt: 2 }}>
              {testResult}
            </Alert>
          )}
        </Paper>

        <Paper elevation={3} sx={{ p: 3, mt: 3 }}>
          <Typography variant="h5" gutterBottom>
            Project Setup Complete!
          </Typography>
          <Typography variant="body1" sx={{ mb: 2 }}>
            This project is configured with:
          </Typography>
          <ul>
            <li>
              <Typography variant="body2">Firebase Backend (Project ID: planapus-d83dd)</Typography>
            </li>
            <li>
              <Typography variant="body2">GraphQL API with Apollo Server</Typography>
            </li>
            <li>
              <Typography variant="body2">React Frontend with Material-UI</Typography>
            </li>
            <li>
              <Typography variant="body2">Apollo Client for GraphQL queries</Typography>
            </li>
            <li>
              <Typography variant="body2">TypeScript support</Typography>
            </li>
          </ul>
        </Paper>

        <Paper elevation={3} sx={{ p: 3, mt: 3 }}>
          <Typography variant="h5" gutterBottom>
            API Endpoints
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {isDevelopment ? '🟢 Development Mode (Emulator)' : '🔴 Production Mode'}
          </Typography>

          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2" gutterBottom>
              GraphQL API:
            </Typography>
            <Link
              href={graphqlUrl}
              target="_blank"
              rel="noopener noreferrer"
              sx={{ wordBreak: 'break-all', fontSize: '0.875rem' }}
            >
              {graphqlUrl}
            </Link>
          </Box>

          <Divider sx={{ my: 2 }} />

          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2" gutterBottom>
              Hello World Function:
            </Typography>
            <Link
              href={helloWorldUrl}
              target="_blank"
              rel="noopener noreferrer"
              sx={{ wordBreak: 'break-all', fontSize: '0.875rem' }}
            >
              {helloWorldUrl}
            </Link>
          </Box>

          <Divider sx={{ my: 2 }} />
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2" gutterBottom>
              Queue Dashboard (Bull Board):
            </Typography>
            <Link
              href={queueDashboardUrl}
              target="_blank"
              rel="noopener noreferrer"
              sx={{ wordBreak: 'break-all', fontSize: '0.875rem' }}
            >
              {queueDashboardUrl}
            </Link>
            <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 0.5 }}>
              Requires admin (OWNER) auth. Pass Bearer token in Authorization header.
            </Typography>
          </Box>

          {isDevelopment && (
            <>
              <Divider sx={{ my: 2 }} />
              <Box>
                <Typography variant="subtitle2" gutterBottom>
                  Firebase Emulator UI:
                </Typography>
                <Link
                  href="http://localhost:4000"
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{ fontSize: '0.875rem' }}
                >
                  http://localhost:4000
                </Link>
              </Box>
            </>
          )}
        </Paper>
      </Box>
    </Container>
  );
}

export default DebugPage;
